import type { ReactNode } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

type LegalKind = "terms" | "privacy";

export function LegalDialog({
  open,
  onOpenChange,
  kind,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  kind: LegalKind;
}) {
  const isTerms = kind === "terms";
  const title = isTerms ? "Terms of Service" : "Privacy Policy";
  const description = isTerms
    ? "The terms that govern use of Lumvio."
    : "How Lumvio collects, uses, and protects your data.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-border/60 bg-gradient-to-b from-muted/40 to-transparent px-6 py-5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/80">Legal</p>
          <DialogTitle className="mt-1 text-2xl font-semibold tracking-tight">{title}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {description} · Last updated July 9, 2026
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh]">
          <article className="px-6 py-6 text-sm leading-relaxed text-muted-foreground [&_h2]:mt-6 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-foreground [&_p]:mt-2 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:ps-5 [&_li]:my-1 [&_a]:text-primary hover:[&_a]:underline">
            {isTerms ? <TermsBody /> : <PrivacyBody />}
          </article>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function TermsBody(): ReactNode {
  return (
    <>
      <p>
        Welcome to Lumvio. These Terms of Service govern your access to and use of the Lumvio website, applications, and
        services, a Payment Protection marketplace connecting verified freelancers with clients.
      </p>
      <h2>1. Acceptance of Terms</h2>
      <p>
        By creating an account or using the Service, you agree to be bound by these Terms. You must be at least 18 years
        old and legally capable of entering into a binding contract.
      </p>
      <h2>2. Accounts and Identity Verification</h2>
      <p>
        Freelancer accounts require successful identity verification through Stripe Identity. You agree to provide
        accurate information and are responsible for all activity under your account.
      </p>
      <h2>3. Marketplace Role</h2>
      <p>
        Lumvio is a neutral platform. Contracts are between clients and freelancers directly; Lumvio is not a party to
        them.
      </p>
      <h2>4. Payment Protection and Payments</h2>
      <ul>
        <li>Clients fund milestones into a secure holding before work begins.</li>
        <li>Funds are released to freelancers upon client approval.</li>
        <li>Lumvio charges a service fee disclosed at the point of transaction.</li>
        <li>Payouts are subject to KYC verification and applicable holds.</li>
      </ul>
      <h2>5. Prohibited Conduct</h2>
      <ul>
        <li>Circumventing Payment Protection or transacting off-platform for platform-sourced work.</li>
        <li>Providing false identity information or impersonation.</li>
        <li>Uploading unlawful, infringing, or deceptive content.</li>
        <li>Using the Service to launder funds or violate applicable law.</li>
      </ul>
      <h2>6. Disputes</h2>
      <p>
        Users are encouraged to resolve disputes directly. Where escalation is required, Lumvio may review evidence and
        make a binding release or refund decision.
      </p>
      <h2>7. Intellectual Property</h2>
      <p>
        Freelancers retain ownership until full payment is released from secure holding, at which point ownership transfers to
        the client unless agreed otherwise in writing.
      </p>
      <h2>8. Termination</h2>
      <p>
        We may suspend or terminate your account for violation of these Terms, suspected fraud, or risk to other users.
        Obligations relating to open contracts survive termination.
      </p>
      <h2>9. Disclaimers</h2>
      <p>The Service is provided "as is" without warranties of any kind, to the maximum extent permitted by law.</p>
      <h2>10. Limitation of Liability</h2>
      <p>
        Lumvio's aggregate liability will not exceed the greater of (a) fees paid to Lumvio in the 12 months preceding
        the claim, or (b) USD 100.
      </p>
      <h2>11. Changes</h2>
      <p>We may update these Terms. Material changes will be notified through the Service.</p>
      <h2>12. Contact</h2>
      <p>
        For questions about these Terms, contact us at <a href="mailto:support@lumvio.co">support@lumvio.co</a>.
      </p>
    </>
  );
}

function PrivacyBody(): ReactNode {
  return (
    <>
      <p>
        This Privacy Policy explains how Lumvio collects, uses, discloses, and protects personal information when you
        use the Service.
      </p>
      <h2>1. Information We Collect</h2>
      <ul>
        <li>
          <strong>Account data:</strong> name, email, role, password hash.
        </li>
        <li>
          <strong>Identity data:</strong> ID and selfie processed by Stripe Identity. We store the result and a session
          reference, not raw images.
        </li>
        <li>
          <strong>Profile and work data:</strong> avatar, bio, skills, portfolio, project briefs, messages, files.
        </li>
        <li>
          <strong>Transaction data:</strong> Payment Protection milestones, invoices, wallet balances, payout metadata.
        </li>
        <li>
          <strong>Technical data:</strong> IP, device, browser, cookies, logs.
        </li>
      </ul>
      <h2>2. How We Use Information</h2>
      <ul>
        <li>Provide, secure, and improve the Service.</li>
        <li>Verify identity and prevent fraud and money laundering.</li>
        <li>Process payments and release Payment Protection milestones.</li>
        <li>Communicate about your account and transactions.</li>
        <li>Comply with legal obligations.</li>
      </ul>
      <h2>3. Sharing</h2>
      <ul>
        <li>
          <strong>Service providers</strong> such as Stripe and Supabase.
        </li>
        <li>
          <strong>Other users</strong> to perform contracts you enter into.
        </li>
        <li>
          <strong>Authorities</strong> when required by law.
        </li>
      </ul>
      <p>We do not sell personal information.</p>
      <h2>4. International Transfers</h2>
      <p>
        Data may be processed in countries other than your own, with appropriate safeguards such as Standard Contractual
        Clauses.
      </p>
      <h2>5. Retention</h2>
      <p>
        We keep account and transaction data as long as your account is active and afterwards as needed to comply with
        legal obligations.
      </p>
      <h2>6. Your Rights</h2>
      <p>
        You may have rights to access, correct, delete, restrict, or port your data. Email{" "}
        <a href="mailto:support@lumvio.co">support@lumvio.co</a>.
      </p>
      <h2>7. Security</h2>
      <p>We use encryption in transit, row-level access controls, and least-privilege administrative access.</p>
      <h2>8. Cookies</h2>
      <p>
        We use strictly necessary cookies for authentication and limited analytics cookies. Control them via your
        browser settings.
      </p>
      <h2>9. Children</h2>
      <p>The Service is not directed to children under 18.</p>
      <h2>10. Changes</h2>
      <p>We may update this Policy. Material changes will be notified through the Service.</p>
      <h2>11. Contact</h2>
      <p>
        For privacy questions, contact us at <a href="mailto:support@lumvio.co">support@lumvio.co</a>.
      </p>
    </>
  );
}
